import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Divider, Label, Loader } from "semantic-ui-react";
import styled from "styled-components";

import { OpenAPI } from "simplydo/interfaces";
import { VideoPlayer } from "simplydo/react/components/VideoPlayer";

import { useAppSelector } from "store";
import util from "utils/utils";

import { EmptyBox, MultiLineParagraph } from "components/lib/UI";
import AchievementComponent from "components/lib/Users/Achievement";

const ProfileDiv = styled.div`
  .overview {
    font-size: 1.2rem;
  }
  .achievements {
    margin-top: 10px;
    .achievements-title {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      h3,
      h5 {
        display: block;
        margin-bottom: 0;
        margin-top: 0;
      }
    }
    .achievements-list {
      display: flex;
      gap: 16px;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
    }
  }
`;

type ProfileDetailsProps = {
  profileUser: OpenAPI.Assign<
    OpenAPI.Schemas["User"],
    OpenAPI.Schemas["AssignTag"],
    OpenAPI.Schemas["AssignUserUrls"],
    OpenAPI.Schemas["AssignUserAchievements"]
  >;
  isYourProfile: boolean;
  loading: boolean;
};

export const ProfileDetails = ({
  profileUser,
  isYourProfile,
  loading,
}: ProfileDetailsProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const { overview, video, videoUrl } = profileUser?.profile ?? {};

  return (
    <ProfileDiv>
      {loading ?
        <Loader active />
      : <>
          {(overview || video) && (
            <h3 style={{ textTransform: "uppercase" }}>
              {t("generic.overview")}
            </h3>
          )}
          {videoUrl ?
            <VideoPlayer style={{ margin: "30px auto" }} src={videoUrl} />
          : null}
          {overview ?
            <div className="overview">
              <MultiLineParagraph content={overview} />
            </div>
          : null}
          {(overview || video) && <Divider hidden />}
          {profileUser?.ownerTags?.length ?
            <div style={{ marginTop: 20 }}>
              <h3 style={{ textTransform: "uppercase" }}>
                {t("generic.tags")}
              </h3>
              {profileUser?.ownerTags.map((tag) => (
                <Label
                  style={{ marginTop: 3 }}
                  key={tag._id}
                  size="large"
                  circular
                >
                  {tag.value}
                </Label>
              ))}
              <Divider hidden />
            </div>
          : null}

          {(isYourProfile ||
            util.hasPermission(
              user,
              "org.viewDashboard",
              user.ownerOrganisation._id,
            )) && (
            <div className="achievements">
              <div className="achievements-title">
                <div>
                  <h3 style={{ textTransform: "uppercase" }}>
                    {t("generic.achievements")}
                  </h3>
                </div>
                <div>
                  <Link className="with-border" to={"/achievements"}>
                    View all achievements
                  </Link>
                </div>
              </div>
              {(
                !profileUser?.achievements?.length &&
                !profileUser?.customAchievements?.length
              ) ?
                <EmptyBox title="No achievements unlocked" />
              : <div className="achievements-list">
                  {profileUser?.customAchievements?.map((a) => (
                    <AchievementComponent
                      showAllInfo
                      key={a._id}
                      achievement={a}
                    />
                  ))}
                  {profileUser?.achievements
                    ?.filter((a) => a.name)
                    ?.map((achievement) => (
                      <AchievementComponent
                        showAllInfo
                        key={achievement?.key}
                        achievement={achievement}
                      />
                    ))}
                </div>
              }
            </div>
          )}
        </>
      }
    </ProfileDiv>
  );
};
